import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Calendar, GraduationCap, User, MessageSquare, BookOpen, Clock, LogOut, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  bio: string | null;
  user_type: string;
}

interface AlumniProfile {
  id: string;
  school: string;
  degree: string;
  field_of_study: string;
  company: string;
  title: string;
  expertise: string | null;
}

const UserDashboard = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [alumniProfile, setAlumniProfile] = useState<AlumniProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          navigate("/auth");
          return;
        }
        
        // Get user profile
        const { data: profileData, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();
        
        if (error) {
          throw error;
        }
        
        setProfile(profileData);
        
        // If user is alumni, get additional alumni profile data
        if (profileData.user_type === 'alumni') {
          const { data: alumniData, error: alumniError } = await supabase
            .from("alumni_profiles")
            .select("id, school, degree, field_of_study, company, title, expertise")
            .eq("id", session.user.id)
            .single();
          
          if (!alumniError) {
            setAlumniProfile(alumniData);
          }
          
          // Get services from the new alumni_services table
          const { data: servicesData } = await supabase
            .from('alumni_services')
            .select('*')
            .eq('alumni_id', session.user.id);
            
          setServices(servicesData || []);
        }
      } catch (error: any) {
        console.error("Error loading profile:", error);
        toast({
          title: "Error loading profile",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [navigate, toast]);
  
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Signed out successfully",
        description: "You have been logged out of your account.",
      });
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <Header />
        <Loader2 className="h-8 w-8 animate-spin text-crimson mb-4" />
        <p>Loading your dashboard...</p>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <Header />
        <p className="text-red-500">Unable to load profile</p>
        <Button onClick={() => navigate("/auth")} className="mt-4">Return to Login</Button>
        <Footer />
      </div>
    );
  }

  const getInitials = () => {
    return `${profile.first_name?.charAt(0) || ''}${profile.last_name?.charAt(0) || ''}`;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={profile.avatar_url || ""} alt={`${profile.first_name} ${profile.last_name}`} />
              <AvatarFallback className="bg-crimson text-white text-xl">{getInitials()}</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-playfair text-3xl font-bold">Welcome, {profile.first_name}</h1>
              <p className="text-gray-600">{profile.user_type === "alumni" ? "Alumni" : "Student"}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="flex items-center gap-2 mt-4 md:mt-0"
            onClick={handleLogout}
          >
            <LogOut size={16} />
            Sign Out
          </Button>
        </div>
        
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <MessageSquare size={18} className="text-crimson" />
                    Recent Messages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-500">You have no new messages</p>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" onClick={() => navigate("/messages")} className="w-full">
                    View All Messages
                  </Button>
                </CardFooter>
              </Card>
              
              {profile.user_type === "alumni" ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-medium flex items-center gap-2">
                      <GraduationCap size={18} className="text-crimson" />
                      Your Services
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {services && services.length > 0 ? (
                      <div className="space-y-2">
                        {services.slice(0, 3).map((service: any) => (
                          <div key={service.id} className="flex justify-between">
                            <span>{service.title}</span>
                            <span className="font-medium">${service.price}</span>
                          </div>
                        ))}
                        {services.length > 3 && (
                          <p className="text-sm text-gray-500">+{services.length - 3} more services</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500">You haven't added any services</p>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button variant="outline" onClick={() => navigate("/services-setup")} className="w-full">
                      {services && services.length > 0 ? "Manage" : "Add"} Services
                    </Button>
                  </CardFooter>
                </Card>
              ) : (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-medium flex items-center gap-2">
                      <User size={18} className="text-crimson" />
                      Saved Alumni
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-500">You haven't saved any alumni yet</p>
                  </CardContent>
                  <CardFooter>
                    <Button variant="outline" onClick={() => navigate("/browse")} className="w-full">
                      Browse Alumni
                    </Button>
                  </CardFooter>
                </Card>
              )}
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <Calendar size={18} className="text-crimson" />
                    Upcoming Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-500">You have no upcoming sessions</p>
                </CardContent>
                <CardFooter>
                  {profile.user_type === "student" ? (
                    <Button variant="outline" onClick={() => navigate("/browse")} className="w-full">
                      Find Alumni
                    </Button>
                  ) : (
                    <Button variant="outline" className="w-full">
                      Manage Availability
                    </Button>
                  )}
                </CardFooter>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle>Your Bookings</CardTitle>
                <CardDescription>View all your upcoming and past sessions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Clock size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-lg text-gray-500">No bookings yet</p>
                  {profile.user_type === "student" ? (
                    <Button onClick={() => navigate("/browse")} className="mt-4 bg-crimson hover:bg-crimson-dark">
                      Find Alumni to Book
                    </Button>
                  ) : (
                    <p className="text-sm text-gray-400 mt-2">When students book your services, they'll appear here</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Messages</CardTitle>
                <CardDescription>View your conversations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-lg text-gray-500">No messages yet</p>
                  <Button onClick={() => navigate("/messages")} className="mt-4 bg-crimson hover:bg-crimson-dark">
                    Go to Messages
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Your Profile</CardTitle>
                <CardDescription>Manage your profile information</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-500">First Name</p>
                        <p>{profile.first_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Last Name</p>
                        <p>{profile.last_name}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium">Bio</h3>
                    <p className="mt-2">{profile.bio || "No bio added yet."}</p>
                  </div>
                  
                  {profile.user_type === "alumni" && alumniProfile && (
                    <div>
                      <h3 className="text-lg font-medium">Alumni Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-500">School</p>
                          <p>{alumniProfile.school}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Degree</p>
                          <p>{alumniProfile.degree}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Field of Study</p>
                          <p>{alumniProfile.field_of_study}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Company</p>
                          <p>{alumniProfile.company}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Title</p>
                          <p>{alumniProfile.title}</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button 
                          onClick={() => navigate("/complete-profile")} 
                          className="bg-crimson hover:bg-crimson-dark"
                        >
                          Edit Alumni Profile
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {profile.user_type === "alumni" && !alumniProfile && (
                    <div>
                      <h3 className="text-lg font-medium">Complete Your Alumni Profile</h3>
                      <p className="text-gray-500 mt-2">Your alumni profile is incomplete. Complete it to offer services to students.</p>
                      <div className="mt-4">
                        <Button 
                          onClick={() => navigate("/complete-profile")} 
                          className="bg-crimson hover:bg-crimson-dark"
                        >
                          Complete Profile
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default UserDashboard;
